import { ChangeEvent } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { resources } from "@constants/hardcoded-strings";
import { BOOLEAN_RADIO_INPUT_OPTIONS } from "@constants/boolean-radio-input-options";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { AXNUIContent, AXNUIContentFooter, AXNUITheme, AXNUIThemeFooter } from "@iddataweb/idw-common-ui/types";
import { ThemingFooterValidationErrors } from "@idw-types/common/validation/common/theming-validation-errors";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { RadioInputFormGroup } from "../../../../../common/radio-input-form-group/radio-input-form-group";
import { TextareaFormGroup } from "../../../../../common/textarea-form-group/textarea-form-group";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { PageLevelTypes } from "@enums/page-level-types";

type ThemingFooterCustomSectionProps = {
    layout: LayoutTypes;
    labelWidth: LabelWidthTypes;
    isDisabled: boolean;
    axnuiContent: AXNUIContent,
    axnuiTheme: AXNUITheme;
    inheritedAXNUIContent: AXNUIContent;
    inheritedAXNUITheme: AXNUITheme;
    pageLevel: PageLevelTypes;
    validationErrors: ThemingFooterValidationErrors;
    updateAXNUIContentFooterFieldValue: (key: keyof AXNUIContentFooter, value: string) => void;
    updateAXNUIThemeFooterFieldValue: (key: keyof AXNUIThemeFooter, value: string | boolean) => void;
};

export const ThemingFooterCustomSection = (props: ThemingFooterCustomSectionProps) => {
    const {
        layout,
        labelWidth,
        isDisabled,
        axnuiContent,
        axnuiTheme,
        inheritedAXNUIContent,
        inheritedAXNUITheme,
        pageLevel,
        validationErrors,
        updateAXNUIContentFooterFieldValue,
        updateAXNUIThemeFooterFieldValue
    } = props;

    const handleUseCustomHtmlAndCssChange = (newValue: string) => {
        updateAXNUIThemeFooterFieldValue("useHtmlCss", isStringTrue(newValue));
    };

    const handleResetUseCustomHtmlAndCss = () => {
        updateAXNUIThemeFooterFieldValue("useHtmlCss", inheritedAXNUITheme.footer.useHtmlCss);
    };

    const handleCustomHtmlChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUIContentFooterFieldValue("customHtmlFooter", event.target.value);
    };

    const handleResetCustomHtml = () => {
        updateAXNUIContentFooterFieldValue("customHtmlFooter", inheritedAXNUIContent.footer.customHtmlFooter);
    };

    const handleCustomCssChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUIContentFooterFieldValue("customCssFooter", event.target.value);
    };

    const handleResetCustomCss = () => {
        updateAXNUIContentFooterFieldValue("customCssFooter", inheritedAXNUIContent.footer.customCssFooter);
    };

    return (
        <div>
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUITheme.footer.useHtmlCss}
                currentThemingPropertyValue={axnuiTheme.footer.useHtmlCss}
                pageLevel={pageLevel}
            >
                <RadioInputFormGroup
                    controlId={`theming-footer-use-custom-html-css-radio-input`}
                    automationId={`theming-footer-use-custom-html-css-radio-input`}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.footer.sections.customHtmlAndCss.inputs.useCustomHtmlAndCss.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleUseCustomHtmlAndCssChange}
                    options={BOOLEAN_RADIO_INPUT_OPTIONS}
                    value={axnuiTheme.footer.useHtmlCss ? "true" : "false"}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.footer.sections.customHtmlAndCss.inputs.useCustomHtmlAndCss.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetUseCustomHtmlAndCss}
                />
            </ThemingFormGroupWrapper>
            {axnuiTheme.footer.useHtmlCss && (
                <>
                    <ThemingFormGroupWrapper
                        inheritedThemingPropertyValue={inheritedAXNUIContent.footer.customHtmlFooter}
                        currentThemingPropertyValue={axnuiContent.footer.customHtmlFooter}
                        pageLevel={pageLevel}
                    >
                        <TextareaFormGroup
                            controlId={"theming-footer-custom-html-input"}
                            automationId={"theming-footer-custom-html-input"}
                            label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.footer.sections.customHtmlAndCss.inputs.customHtml.label}
                            ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.footer.sections.customHtmlAndCss.inputs.customHtml.label}
                            layout={layout}
                            labelWidth={labelWidth}
                            onChange={handleCustomHtmlChange}
                            value={axnuiContent.footer.customHtmlFooter || ""}
                            disabled={isDisabled}
                            isRequired={true}
                            afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.footer.sections.customHtmlAndCss.inputs.customHtml.tooltip}
                            afterLabelIcon={isDisabled ? null : faUndo}
                            afterLabelIconOnClick={handleResetCustomHtml}
                        />
                    </ThemingFormGroupWrapper>
                    {validationErrors.customHtmlError}

                    <ThemingFormGroupWrapper
                        inheritedThemingPropertyValue={inheritedAXNUIContent.footer.customCssFooter}
                        currentThemingPropertyValue={axnuiContent.footer.customCssFooter}
                        pageLevel={pageLevel}
                    >
                        <TextareaFormGroup
                            controlId={"theming-footer-custom-css-input"}
                            automationId={"theming-footer-custom-css-input"}
                            label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.footer.sections.customHtmlAndCss.inputs.customCss.label}
                            ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.footer.sections.customHtmlAndCss.inputs.customCss.label}
                            layout={layout}
                            labelWidth={labelWidth}
                            onChange={handleCustomCssChange}
                            value={axnuiContent.footer.customCssFooter || ""}
                            disabled={isDisabled}
                            isRequired={true}
                            afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.footer.sections.customHtmlAndCss.inputs.customCss.tooltip}
                            afterLabelIcon={isDisabled ? null : faUndo}
                            afterLabelIconOnClick={handleResetCustomCss}
                        />
                    </ThemingFormGroupWrapper>
                    {validationErrors.customCssError}
                </>
            )}
        </div>
    );
};